import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { OwnerEmptyScaffold } from '../kit/OwnerEmptyScaffold'
import { OwnerTable, type ColumnDef } from '../kit/OwnerTable'
import { navigateOwner, ownerRouteToPath, type OwnerRoute } from '../ownerRouter'
import { useAuth } from '../../../context/useAuth'
import { useNls } from '../../../nls/nlsProvider'
import type { NlsKey } from '../../../nls/strings_nls'
import {
  listOwnerWorkspacesV2,
  type OwnerWorkspaceListItem,
  type WorkspaceSavedViewId,
  type WorkspaceSort,
} from '../../../services/ownerWorkspacesService'
import { runOwnerAction, type OwnerActionInput } from '../../../services/ownerConsoleService'
import { OwnerWorkspaceDetailPanel } from './OwnerWorkspaceDetailPanel'
import './ownerWorkspaces.css'

type WorkspaceMetadata = { orderingChanged?: boolean; search?: { substringCap?: number; truncated?: boolean; complete?: boolean } }

const SAVED_VIEWS: Array<{ id: WorkspaceSavedViewId; key: NlsKey }> = [
  { id: 'all', key: 'NLSOwnerConsoleWorkspaceViewAll' },
  { id: 'needs-attention', key: 'NLSOwnerConsoleWorkspaceViewNeedsAttention' },
  { id: 'enterprise', key: 'NLSOwnerConsoleWorkspaceViewEnterprise' },
  { id: 'credit-empty', key: 'NLSOwnerConsoleWorkspaceViewCreditEmpty' },
  { id: 'has-open-tickets', key: 'NLSOwnerConsoleWorkspaceViewOpenTickets' },
  { id: 'revenue-modeled', key: 'NLSOwnerConsoleWorkspaceViewRevenueModeled' },
]

const validView = (value: string | undefined): WorkspaceSavedViewId =>
  SAVED_VIEWS.some(({ id }) => id === value) ? value as WorkspaceSavedViewId : 'all'
const validSort = (value: string | undefined): WorkspaceSort =>
  value === 'mrrModeled' || value === 'riskTier' ? value : 'lastActiveAt'
const formatNumber = (value: number | null) => value === null ? '—' : value.toLocaleString('en-IN')
const formatCurrency = (value: number | null) => value === null || value <= 0 ? '—' : `₹${value.toLocaleString('en-IN')}`
const formatStorage = (value: number | null) => value === null ? null : value / 1024 ** 3
const relativeTime = (iso: string | null) => {
  if (!iso || Number.isNaN(new Date(iso).getTime())) return '—'
  const hours = Math.round((Date.now() - new Date(iso).getTime()) / 3_600_000)
  if (hours < 24) return `${Math.max(0, hours)} h ago`
  const days = Math.round(hours / 24)
  return days < 7 ? `${days} d ago` : new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * The indexed list owns its hooks. Detail routing is intentionally handled by
 * OwnerConsole so a list-to-detail transition never changes this component's
 * hook order in place.
 */
export function OwnerWorkspacesListPanel({
  route,
  onSubmit,
}: {
  route: Extract<OwnerRoute, { view: 'workspaces' }>
  onSubmit?: (input: OwnerActionInput) => Promise<{ ok: true; data?: Record<string, unknown> } | { ok: false; message: string }>
}) {
  const { t } = useNls()
  const { currentUser } = useAuth()

  const submitHandler = onSubmit || (async (input: OwnerActionInput) => {
    try {
      const res = await runOwnerAction(input)
      return res
    } catch (err: unknown) {
      return { ok: false, message: err instanceof Error ? err.message : 'Action failed' }
    }
  })

  const query = useMemo(() => route.query || {}, [route.query])
  const savedView = validView(query.savedView)
  const plan = query.plan || 'ALL'
  const type = query.type === 'TEAM' || query.type === 'PERSONAL' ? query.type : 'ALL'
  const status = query.status || 'ALL'
  const sortBy = savedView === 'revenue-modeled' ? 'mrrModeled' : validSort(query.sortBy)
  const sortDir = query.sortDir === 'asc' ? 'asc' : 'desc'
  const search = query.q || ''
  const filterKey = `${search}|${plan}|${type}|${status}|${savedView}|${sortBy}|${sortDir}`
  const [rows, setRows] = useState<OwnerWorkspaceListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cursor, setCursor] = useState<string | null>(null)
  const [cursorFilterKey, setCursorFilterKey] = useState<string | null>(null)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageCursors, setPageCursors] = useState<Array<string | null>>([null])
  const [asideWorkspaceId, setAsideWorkspaceId] = useState<string | null>(null)
  const [metadata, setMetadata] = useState<WorkspaceMetadata>({})
  const requestVersion = useRef(0)

  const updateQuery = useCallback((patch: Record<string, string | undefined>) => {
    const next = { ...query, ...patch }
    Object.keys(next).forEach((key) => { if (!next[key]) delete next[key] })
    navigateOwner({ view: 'workspaces', query: next })
  }, [query])

  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey)
    setCursor(null)
    setCursorFilterKey(filterKey)
    setNextCursor(null)
    setPage(1)
    setPageCursors([null])
    setLoading(true)
    setError(null)
    setMetadata({})
  }
  const requestCursor = cursorFilterKey === filterKey ? cursor : null

  useEffect(() => {
    let active = true
    const version = ++requestVersion.current
    void listOwnerWorkspacesV2({
      search: search || undefined,
      plan: plan === 'ALL' ? undefined : plan,
      type: type === 'ALL' ? undefined : type,
      status: status === 'ALL' ? undefined : status,
      preset: savedView,
      sortBy,
      sortDir,
      cursor: requestCursor,
      limit: 50,
    })
      .then((result) => {
        if (!active || version !== requestVersion.current) return
        setRows(result.workspaces)
        setNextCursor(result.nextCursor)
        setMetadata({ orderingChanged: result.orderingChanged, search: result.search })
        setLoading(false)
      })
      .catch((reason: unknown) => {
        if (!active || version !== requestVersion.current) return
        setError(reason instanceof Error ? reason.message : 'load-failed')
        setLoading(false)
      })
    return () => {
      active = false
    }
  }, [requestCursor, savedView, plan, type, status, search, sortBy, sortDir])

  const detailQuery = useMemo(
    () => ({ tab: 'overview', returnQuery: encodeURIComponent(JSON.stringify(query)) }),
    [query],
  )

  const columns: ColumnDef<OwnerWorkspaceListItem>[] = useMemo(() => [
    { key: 'workspace', header: t('NLSOwnerConsoleWorkspaceColumnWorkspace'), width: 230, render: (row) => (
      <div className="ownerWorkspaceMain">
        <a
          href={ownerRouteToPath({ view: 'workspace-detail', workspaceId: row.id, query: detailQuery })}
          className="ownerWorkspaceLink"
          onClick={(e) => {
            e.stopPropagation()
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
            e.preventDefault()
            navigateOwner({ view: 'workspace-detail', workspaceId: row.id, query: detailQuery })
          }}
        >
          <strong>{row.name || t('NLSOwnerConsoleWorkspaceMissing')}</strong>
        </a>
        <small>{row.shortId || '—'} · {row.type === 'PERSONAL' ? t('NLSOwnerConsoleWorkspacePersonal') : t('NLSOwnerConsoleWorkspaceTeam')}</small>
        {row.demoUnlockEnabled === true && <span className="ownerWorkspaceDemoBadge" data-testid="owner-workspace-demo-badge">{t('NLSOwnerConsoleWorkspaceDemoBadge')}</span>}
      </div>
    ) },
    { key: 'plan', header: t('NLSOwnerConsoleWorkspaceColumnPlan'), width: 130, render: (row) => <span>{row.plan || '—'}</span> },
    { key: 'owner', header: t('NLSOwnerConsoleWorkspaceColumnOwner'), width: 210, render: (row) => <span>{row.ownerEmail || '—'}</span> },
    { key: 'people', header: t('NLSOwnerConsoleWorkspaceColumnPeople'), width: 115, render: (row) => <span title={t('NLSOwnerConsoleWorkspacePeopleTooltip', { members: row.activeMemberCount, helpers: row.helperCount })}>{formatNumber(row.activeMemberCount)} + {formatNumber(row.helperCount)}H</span> },
    { key: 'storage', header: t('NLSOwnerConsoleWorkspaceColumnStorage'), width: 145, render: (row) => {
      const used = formatStorage(row.storageUsedBytes); const quota = formatStorage(row.storagePlanBytes)
      if (used === null || quota === null || quota <= 0) return <span>—</span>
      const percent = Math.min(100, Math.round(used / quota * 100))
      return <div className="ownerWorkspaceStorage" title={`${used.toFixed(2)} GB / ${quota.toFixed(2)} GB`}><span>{used.toFixed(2)}/{quota.toFixed(2)} GB</span><i><b style={{ width: `${percent}%` }} /></i></div>
    } },
    { key: 'credits', header: t('NLSOwnerConsoleWorkspaceColumnCredits'), width: 110, render: (row) => <span title={row.creditsSpentLifetime === null ? undefined : t('NLSOwnerConsoleWorkspaceCreditsTooltip', { spent: formatNumber(row.creditsSpentLifetime) })}>{formatNumber(row.creditsAvailable)}</span> },
    { key: 'mrr', header: t('NLSOwnerConsoleWorkspaceColumnMrr'), width: 145, render: (row) => <span title={t('NLSOwnerConsoleWorkspaceModeledTooltip')}>{formatCurrency(row.mrrModeled)} <small className="ownerWorkspaceEstimate">{t('NLSOwnerConsoleWorkspaceModeled')}</small></span> },
    { key: 'risk', header: t('NLSOwnerConsoleWorkspaceColumnRisk'), width: 140, render: (row) => <span className={`ownerWorkspaceRisk ownerWorkspaceRisk--${(row.riskTier || 'unknown').toLowerCase()}`} title={t('NLSOwnerConsoleWorkspaceRiskTooltip')}>{row.riskTier || '—'} {row.riskTier && <small>{t('NLSOwnerConsoleWorkspaceHeuristic')}</small>}</span> },
    { key: 'lastActive', header: t('NLSOwnerConsoleWorkspaceColumnLastActive'), width: 130, render: (row) => <time title={row.lastActiveAt ? new Date(row.lastActiveAt).toLocaleString('en-GB', { timeZoneName: 'short' }) : undefined}>{relativeTime(row.lastActiveAt)}</time> },
  ], [t, detailQuery])
  const userScope = currentUser?.uid || 'anonymous'
  const visibilityStorageKey = `timelining_owner_workspace_columns_${userScope}_${savedView}`

  return <section className="ownerWorkspacesContainer" data-testid="owner-workspaces-panel">
    {/* SEAM: custom saved views and cohorts belong to Phase 3 / chapter 15. */}
    {/* SEAM: modeled revenue is replaced by the Phase 7 / chapter 17 ledger. */}
    <header className="ownerWorkspacesHeader"><div><h1>{t('NLSOwnerConsoleWorkspaceTitle')}</h1><p>{t('NLSOwnerConsoleWorkspaceSubtitle')}</p></div><nav aria-label={t('NLSOwnerConsoleWorkspaceViewsLabel')}>{SAVED_VIEWS.map((view) => <button key={view.id} type="button" className={savedView === view.id ? 'ownerWorkspaceView--active' : ''} onClick={() => updateQuery({ savedView: view.id === 'all' ? undefined : view.id })}>{t(view.key)}</button>)}</nav></header>
    <div className="ownerWorkspacesFilters">
      <input aria-label={t('NLSOwnerConsoleWorkspaceSearchLabel')} placeholder={t('NLSOwnerConsoleWorkspaceSearchPlaceholder')} value={search} onChange={(event) => updateQuery({ q: event.target.value || undefined })} />
      <select aria-label={t('NLSOwnerConsoleWorkspacePlanLabel')} value={plan} onChange={(event) => updateQuery({ plan: event.target.value === 'ALL' ? undefined : event.target.value })}><option value="ALL">{t('NLSOwnerConsoleWorkspaceAllPlans')}</option><option value="FREE">{t('NLSOwnerConsoleWorkspacePlanFree')}</option><option value="ESSENTIAL">{t('NLSOwnerConsoleWorkspacePlanEssential')}</option><option value="GROWTH">{t('NLSOwnerConsoleWorkspacePlanGrowth')}</option><option value="PROFESSIONAL">{t('NLSOwnerConsoleWorkspacePlanProfessional')}</option><option value="ENTERPRISE">{t('NLSOwnerConsoleWorkspacePlanEnterprise')}</option></select>
      <select aria-label={t('NLSOwnerConsoleWorkspaceTypeLabel')} value={type} onChange={(event) => updateQuery({ type: event.target.value === 'ALL' ? undefined : event.target.value })}><option value="ALL">{t('NLSOwnerConsoleWorkspaceAllTypes')}</option><option value="TEAM">{t('NLSOwnerConsoleWorkspaceTeam')}</option><option value="PERSONAL">{t('NLSOwnerConsoleWorkspacePersonal')}</option></select>
      <select aria-label={t('NLSOwnerConsoleWorkspaceStatusLabel')} value={status} onChange={(event) => updateQuery({ status: event.target.value === 'ALL' ? undefined : event.target.value })}><option value="ALL">{t('NLSOwnerConsoleWorkspaceAllStatuses')}</option><option value="ACTIVE">{t('NLSOwnerConsoleWorkspaceStatusActive')}</option><option value="SUSPENDED">{t('NLSOwnerConsoleWorkspaceStatusSuspended')}</option><option value="TRIAL_EXPIRED">{t('NLSOwnerConsoleWorkspaceStatusTrialExpired')}</option></select>
      <select aria-label={t('NLSOwnerConsoleWorkspaceSortLabel')} value={sortBy} disabled={savedView === 'revenue-modeled'} onChange={(event) => updateQuery({ sortBy: event.target.value })}><option value="lastActiveAt">{t('NLSOwnerConsoleWorkspaceSortLastActive')}</option><option value="mrrModeled">{t('NLSOwnerConsoleWorkspaceSortRevenue')}</option><option value="riskTier">{t('NLSOwnerConsoleWorkspaceSortRisk')}</option></select>
    </div>
    {metadata.search?.truncated && <p className="ownerWorkspaceNotice">{t('NLSOwnerConsoleWorkspaceSearchTruncated', { cap: metadata.search.substringCap || 1000 })}</p>}
    {metadata.orderingChanged && <p className="ownerWorkspaceNotice">{t('NLSOwnerConsoleWorkspaceOrderingChanged')}</p>}
    {loading ? <p>{t('NLSOwnerConsoleWorkspaceLoading')}</p> : error ? <p className="ownerWorkspaceError">{error === 'load-failed' ? t('NLSOwnerConsoleWorkspaceLoadError') : error}</p> : rows.length === 0 ? <OwnerEmptyScaffold title={t('NLSOwnerConsoleWorkspaceEmptyTitle')} body={t('NLSOwnerConsoleWorkspaceEmptyBody')} explanation={t('NLSOwnerConsoleWorkspaceEmptyExplanation')} /> : <OwnerTable key={visibilityStorageKey} data={rows} columns={columns} viewKey="owner-workspaces-server" visibilityStorageKey={visibilityStorageKey} getRowId={(row) => row.id} onRowClick={(row) => navigateOwner({ view: 'workspace-detail', workspaceId: row.id, query: detailQuery })} actionsMenu={(row) => (
      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="ownerWorkspaceAsideBtn"
          title={t('NLSOwnerConsoleWorkspaceAsidePreview')}
          aria-label={t('NLSOwnerConsoleWorkspaceAsidePreviewNamed', { name: row.name || row.id })}
          onClick={() => setAsideWorkspaceId(row.id)}
        >
          {t('NLSOwnerConsoleWorkspaceAside')}
        </button>
        <a
          href={ownerRouteToPath({ view: 'workspace-detail', workspaceId: row.id, query: detailQuery })}
          className="ownerWorkspaceDetailLink"
          title={t('NLSOwnerConsoleWorkspaceOpenDetail')}
          aria-label={t('NLSOwnerConsoleWorkspaceOpenDetailNamed', { name: row.name || row.id })}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
            e.preventDefault()
            navigateOwner({ view: 'workspace-detail', workspaceId: row.id, query: detailQuery })
          }}
        >
          →
        </a>
      </div>
    )} pageSize={200} />}
    {!loading && !error && (rows.length > 0 || nextCursor || page > 1) && <div className="ownerWorkspacePagination" aria-label={t('NLSOwnerConsoleWorkspacePaginationLabel')}><span>{t('NLSOwnerConsoleWorkspacePage', { page })}</span><button type="button" disabled={page === 1} onClick={() => { const previous = pageCursors[page - 2] ?? null; setCursor(previous); setCursorFilterKey(filterKey); setPage((value) => value - 1) }}>{t('NLSOwnerConsolePrevious')}</button><button type="button" disabled={!nextCursor} onClick={() => { if (!nextCursor) return; setPageCursors((current) => [...current.slice(0, page), nextCursor]); setCursor(nextCursor); setCursorFilterKey(filterKey); setPage((value) => value + 1) }}>{t('NLSOwnerConsoleWorkspaceNextPage')}</button></div>}
    {asideWorkspaceId && (
      <aside className="ownerWorkspacesAsideDrawer" aria-label={t('NLSOwnerConsoleWorkspaceAside')}>
        <div className="ownerWorkspacesAsideHeader">
          <button type="button" onClick={() => setAsideWorkspaceId(null)}>{t('NLSOwnerConsoleWorkspaceAsideClose')}</button>
        </div>
        <OwnerWorkspaceDetailPanel
          route={{ view: 'workspace-detail', workspaceId: asideWorkspaceId, query: detailQuery }}
          onSubmit={submitHandler}
          isAside
          onClose={() => setAsideWorkspaceId(null)}
          onSelectWorkspace={(nextId) => setAsideWorkspaceId(nextId)}
        />
      </aside>
    )}
  </section>
}
